/**
 * Veranda-configurator - vertalingen (gedeelde kern)
 *
 * Vertalingen komen uit data/translations.csv (kolommen ID, NL, DE). Welke
 * talen een klant voert, welke taal de standaard is en hoe de taal uit de URL
 * volgt, staat in tenant.config.js, niet hier.
 */
import tenant from './tenant.config.js';

const I18N_CSV_URL = './data/translations.csv';
const CFG = tenant.i18n;
const STORAGE_KEY = CFG.storageKey;
const SUPPORTED_LANGUAGES = CFG.supported;
const DEFAULT_LANGUAGE = CFG.default;

let translations = {}; // { id: { nl: '...', de: '...' } }
let currentLang = DEFAULT_LANGUAGE;
let isInitialized = false;

/**
 * Initialize i18n system
 * Call this once on page load
 */
export async function init() {
    if (isInitialized) {
        return;
    }
    
    try {
        await loadTranslations();
        currentLang = detectLanguage();
        document.documentElement.lang = currentLang;
        
        applyTranslations();
        wireLanguageSwitcher();
        updateActiveFlagUI();
        
        isInitialized = true;
    } catch (error) {
        currentLang = DEFAULT_LANGUAGE;
    }
}

/**
 * Set language and persist preference
 * @param {string} lang - Language code ('nl' or 'de')
 */
export function setLanguage(lang) {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
        return;
    }
    
    const previousLang = currentLang;
    
    currentLang = lang;
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
    
    // Update URL with language parameter
    updateURLWithLanguage(lang);
    
    applyTranslations();
    updateActiveFlagUI();
    
    // Dispatch custom event for other modules to listen
    document.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { lang, previousLang } 
    }));
    
    // GTM Event: Language Change
    if (window.dataLayer) {
        window.dataLayer.push({
            event: 'language_change',
            from_lang: previousLang,
            to_lang: lang,
            timestamp: new Date().toISOString()
        });
    }
}

/**
 * Update URL with language parameter
 * @param {string} lang - Language code to add to URL
 */
function updateURLWithLanguage(lang) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    // Set or update the lang parameter
    params.set('lang', lang);
    
    // Update URL without page reload
    const newUrl = `${url.pathname}?${params.toString()}${url.hash}`;
    window.history.replaceState({ lang }, '', newUrl);
}

/**
 * Get current language
 * @returns {string} Current language code
 */
export function getCurrentLanguage() {
    return currentLang;
}

/**
 * Translate a single key
 * @param {string} id - Translation key
 * @returns {string} Translated text
 */
export function t(id) {
    const row = translations[id];
    if (!row) {
        return id;
    }
    return (row[currentLang] || row[DEFAULT_LANGUAGE] || id).trim();
}

/**
 * Load translations from CSV file
 */
async function loadTranslations() {
    try {
        const response = await fetch(I18N_CSV_URL, { 
            cache: 'no-cache',
            headers: { 'Accept': 'text/csv' }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const text = await response.text();
        translations = parseCSV(text);
    } catch (error) {
        throw error;
    }
}

/**
 * Parse CSV text into translations object
 * Handles quoted fields and commas
 * @param {string} text - CSV text
 * @returns {Object} Translations map { id: { nl, de } }
 */
function parseCSV(text) {
    const rows = [];
    let i = 0, field = '', inQuotes = false, row = [];
    
    // Character-by-character parsing for robust handling
    while (i < text.length) {
        const c = text[i++];
        
        if (inQuotes) {
            if (c === '"') {
                // Check for escaped quote ("")
                if (text[i] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += c;
            }
        } else {
            if (c === '"') {
                inQuotes = true;
            } else if (c === ',') {
                row.push(field);
                field = '';
            } else if (c === '\n' || c === '\r') {
                // Handle CRLF
                if (c === '\r' && text[i] === '\n') i++;
                
                row.push(field);
                field = '';
                
                if (row.length) {
                    rows.push(row);
                }
                row = [];
            } else {
                field += c;
            }
        }
    }
    
    // Push last field/row
    if (field.length || row.length) {
        row.push(field);
        rows.push(row);
    }
    
    // Parse headers
    const header = rows.shift() || [];
    const idIdx = header.findIndex(h => h.trim().toLowerCase() === 'id');
    const nlIdx = header.findIndex(h => h.trim().toLowerCase() === 'nl');
    const deIdx = header.findIndex(h => h.trim().toLowerCase() === 'de');
    
    if (idIdx === -1 || nlIdx === -1 || deIdx === -1) {
        throw new Error('CSV must have ID, NL, and DE columns');
    }
    
    // Build translations map
    const map = {};
    rows.forEach(r => {
        const id = (r[idIdx] || '').trim();
        if (!id) return; // Skip empty rows
        
        map[id] = {
            nl: (r[nlIdx] || '').trim(),
            de: (r[deIdx] || '').trim()
        };
    });
    
    return map;
}

/**
 * Bepaal de taal van de bezoeker.
 * Volgorde: ?lang= -> opgeslagen keuze -> verwijzende pagina -> pad -> browser -> standaard.
 *
 * Het pad-schema verschilt per klant:
 *   'nl-prefix'          root = standaardtaal, /nl/ = Nederlands (mimugarten.nl)
 *   'path-and-subdomain' /nl/ en /de/, en ook nl. en de. als subdomein
 */
function detectLanguage() {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && SUPPORTED_LANGUAGES.includes(langParam.toLowerCase())) {
        return langParam.toLowerCase();
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
        return saved;
    }

    const fromSub = CFG.scheme === 'path-and-subdomain'
        ? langFromSubdomain(window.location.hostname)
        : null;
    if (fromSub) return fromSub;

    if (document.referrer) {
        try {
            const ref = new URL(document.referrer);
            const viaPath = langFromPath(ref.pathname);
            if (viaPath) return viaPath;
            if (CFG.scheme === 'path-and-subdomain') {
                const viaSub = langFromSubdomain(ref.hostname);
                if (viaSub) return viaSub;
            }
            // Bij 'nl-prefix' betekent de root van de eigen site de standaardtaal.
            if (CFG.scheme === 'nl-prefix' &&
                ref.hostname.toLowerCase().endsWith(CFG.host)) {
                return DEFAULT_LANGUAGE;
            }
        } catch (e) {
            // onbruikbare referrer; door naar de volgende stap
        }
    }

    const viaOwnPath = langFromPath(location.pathname);
    if (viaOwnPath) return viaOwnPath;

    const nav = (navigator.language || '').toLowerCase();
    for (const lang of SUPPORTED_LANGUAGES) {
        if (nav.startsWith(lang)) return lang;
    }

    return DEFAULT_LANGUAGE;
}

/** Taal uit een pad halen, volgens het schema van deze klant. */
function langFromPath(pathname) {
    const p = (pathname || '').toLowerCase();
    if (CFG.scheme === 'nl-prefix') {
        return (p.startsWith('/nl/') || p === '/nl') ? 'nl' : null;
    }
    for (const lang of SUPPORTED_LANGUAGES) {
        if (p.startsWith('/' + lang + '/') || p === '/' + lang) return lang;
    }
    return null;
}

/** Taal uit een subdomein halen (nl.klant.nl / de.klant.nl). */
function langFromSubdomain(hostname) {
    const h = (hostname || '').toLowerCase();
    for (const lang of SUPPORTED_LANGUAGES) {
        if (h.startsWith(lang + '.')) return lang;
    }
    return null;
}

/**
 * Apply translations to all elements with data-i18n
 * Batched in requestAnimationFrame for performance
 */
function applyTranslations() {
    requestAnimationFrame(() => {
        // Translate text content and attributes
        document.querySelectorAll('[data-i18n], [data-i18n-attr]').forEach(el => {
            const id = el.getAttribute('data-i18n');
            
            // Translate text content if data-i18n is present
            if (id) {
                const value = t(id);
                
                // Check if HTML mode is enabled
                if (el.getAttribute('data-i18n-html') === 'true') {
                    el.innerHTML = value;
                } else {
                    el.textContent = value;
                }
            }
            
            // Translate attributes (placeholder, title, aria-label, etc.)
            const attrs = (el.getAttribute('data-i18n-attr') || '')
                .split('|')
                .map(s => s.trim())
                .filter(Boolean);
            
            for (const attr of attrs) {
                // Support attr:key syntax (e.g., "placeholder:modal_form_address_placeholder")
                if (attr.includes(':')) {
                    const [attrName, attrKey] = attr.split(':').map(s => s.trim());
                    el.setAttribute(attrName, t(attrKey));
                } else if (id) {
                    // Fallback: use same translation as element text (only if data-i18n is present)
                    el.setAttribute(attr, t(id));
                }
            }
        });
        
        // Update all external links to use correct language path
        updateExternalLinks();
        
        // Update logo to correct language
        updateLogo();
    });
}

/**
 * Zet alle links naar de eigen site om naar de huidige taal.
 * Bij 'nl-prefix' krijgt Nederlands /nl/ ervoor en staat de andere taal op de
 * root; bij 'path-and-subdomain' wisselt /nl/ met /de/ of het subdomein mee.
 */
function updateExternalLinks() {
    document.querySelectorAll('a[href*="' + CFG.host + '"]').forEach(link => {
        const href = link.getAttribute('href');
        if (!href) return;

        let newHref = href;
        try {
            const url = new URL(href, window.location.origin);
            if (!url.hostname.toLowerCase().endsWith(CFG.host)) return;

            if (CFG.scheme === 'nl-prefix') {
                const hasNl = url.pathname.startsWith('/nl/') || url.pathname === '/nl';
                if (currentLang === 'nl' && !hasNl) {
                    url.pathname = '/nl' + url.pathname;
                    newHref = url.toString();
                } else if (currentLang !== 'nl' && hasNl) {
                    url.pathname = url.pathname.replace(/^\/nl(\/|$)/, '/');
                    newHref = url.toString();
                }
            } else {
                const sub = langFromSubdomain(url.hostname);
                if (sub) {
                    url.hostname = url.hostname.replace(
                        new RegExp('^' + sub + '\\.'), currentLang + '.');
                    newHref = url.toString();
                } else {
                    for (const lang of SUPPORTED_LANGUAGES) {
                        if (lang === currentLang) continue;
                        newHref = newHref
                            .replace(new RegExp('/' + lang + '/', 'g'), '/' + currentLang + '/')
                            .replace(new RegExp('/' + lang + '$', 'g'), '/' + currentLang);
                    }
                }
            }
        } catch (e) {
            // href niet te ontleden; laat hem staan
        }

        if (newHref !== href) {
            link.setAttribute('href', newHref);
        }
    });
}

/**
 * Logo en home-link volgen de taal en komen uit tenant.config.js.
 *
 * Het contract voor een tenant-fragment is:
 *   data-logo   op elke <img> die het logo toont
 *   data-home   op elke <a> die naar de eigen homepagina wijst
 *
 * De oude klassen (.logo-img, .brand, .brand-footer) blijven werken, zodat de
 * bestaande Webflow-header van Domiveranda ongewijzigd kan blijven. Een nieuwe
 * klant gebruikt de data-attributen; die zeggen wat het IS in plaats van hoe
 * het toevallig heette.
 *
 * Voert een klant maar één logo, dan wijzen beide talen naar hetzelfde bestand.
 */
const KIES_LOGO = '[data-logo], .logo-img';
const KIES_HOME = '[data-home], .brand, .brand-footer';

function updateLogo() {
    const src = CFG.logo[currentLang] || CFG.logo[DEFAULT_LANGUAGE];
    document.querySelectorAll(KIES_LOGO).forEach(img => { img.src = src; });

    const home = CFG.home[currentLang] || CFG.home[DEFAULT_LANGUAGE];
    document.querySelectorAll(KIES_HOME).forEach(link => {
        link.setAttribute('href', home);
    });
}

/**
 * Koppel de taalkiezer. Er zijn twee varianten in omloop: de Webflow-dropdown
 * (.locale-link in een .w-dropdown) en de mimugarten-dropdown (.mg-langswitch).
 * Elke klant levert er één; het blok dat niets vindt doet niets.
 */
function wireLanguageSwitcher() {
    document.querySelectorAll('.lang-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const lang = btn.getAttribute('data-lang');
            if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
                setLanguage(lang);
                closeLangDropdown();
            }
        });
    });

    // Webflow-variant
    document.querySelectorAll('.locale-link').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const href = link.getAttribute('href') || '/' + DEFAULT_LANGUAGE;
            const lang = SUPPORTED_LANGUAGES.find(
                l => href.includes('/' + l) || href === '/' + l) || DEFAULT_LANGUAGE;
            setLanguage(lang);
            const dropdown = link.closest('.w-dropdown');
            const list = dropdown && dropdown.querySelector('.dropdown-list');
            if (list) list.classList.remove('w--open');
        });
    });

    // mimugarten-variant
    const trigger = document.querySelector('.mg-langswitch__current');
    const list = document.querySelector('.mg-langswitch__list');
    if (trigger && list) {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (list.hasAttribute('hidden')) {
                list.removeAttribute('hidden');
                trigger.setAttribute('aria-expanded', 'true');
            } else {
                closeLangDropdown();
            }
        });
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.mg-langswitch')) closeLangDropdown();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') closeLangDropdown();
        });
    }
}

function closeLangDropdown() {
    const trigger = document.querySelector('.mg-langswitch__current');
    const list = document.querySelector('.mg-langswitch__list');
    if (list) list.setAttribute('hidden', '');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
}

/**
 * Update active flag indicator in dropdown
 */
function updateActiveFlagUI() {
    // Update flag buttons - add active class to current language
    const flagButtons = document.querySelectorAll('.lang-btn');
    flagButtons.forEach(btn => {
        const lang = btn.getAttribute('data-lang');
        if (lang === currentLang) {
            btn.classList.add('active');
            // Add visual indicator (e.g., border or opacity)
            btn.style.opacity = '1';
            btn.style.border = '2px solid #4ade80';
        } else {
            btn.classList.remove('active');
            btn.style.opacity = '0.6';
            btn.style.border = '2px solid transparent';
        }
    });
    
    // Remove active state from all locale links
    document.querySelectorAll('.locale-link').forEach(link => {
        link.classList.remove('w--current');
    });
    
    // Add active state to current language link
    // Works for both path-based (/nl, /de) and subdomain-based URLs
    document.querySelectorAll('.locale-link').forEach(link => {
        const href = link.getAttribute('href');
        if (!href) return;
        
        // Check if this link matches current language
        const isNlLink = href.includes('/nl') || href === '/nl';
        const isDeLink = href.includes('/de') || href === '/de';
        
        if ((currentLang === 'nl' && isNlLink) || (currentLang === 'de' && isDeLink)) {
            link.classList.add('w--current');
        }
    });
    
    // Vlag op de taalkiezer zelf. Markeer die in het tenant-fragment met
    // data-flag; .locale-flag blijft werken voor de bestaande Webflow-header.
    // De extensie verschilt per bron (flagcdn levert png, weglot svg) en staat
    // daarom in tenant.config.js.
    const ext = CFG.flagExt || 'png';
    document.querySelectorAll('[data-flag], .dropdown-toggle .locale-flag')
        .forEach(flagImg => {
            flagImg.src = `${CFG.flagBase}${currentLang}.${ext}`;
            flagImg.alt = currentLang.toUpperCase();
        });
}

/**
 * Re-translate dynamically rendered content
 * Call this after modal opens or new content is added to DOM
 */
export function retranslate() {
    if (!isInitialized) return;
    applyTranslations();
}

// Export for debugging in console
if (typeof window !== 'undefined') {
    window.i18n = {
        setLanguage,
        getCurrentLanguage,
        t,
        retranslate
    };
}

export default {
    init,
    setLanguage,
    getCurrentLanguage,
    t,
    retranslate
};
